import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationService } from '@progress/kendo-angular-notification';
import { DialogService } from '@progress/kendo-angular-dialog';
import {
  RouterAdminService,
  RouterTemplate,
  RouterStep,
  WorkCenter
} from '../../../shared/services/router-admin.service';

@Component({
    selector: 'app-router-templates',
    templateUrl: './router-templates.component.html',
    styleUrls: ['./router-templates.component.css'],
    standalone: false
})
export class RouterTemplatesComponent implements OnInit {
  templates: RouterTemplate[] = [];
  selectedTemplate: RouterTemplate | null = null;
  steps: RouterStep[] = [];
  loading = false;
  stepsLoading = false;

  // Edit modes
  isEditingTemplate = false;
  isNewTemplate = false;
  isEditingStep = false;
  isNewStep = false;
  selectedStep: RouterStep | null = null;

  // Forms
  templateForm: FormGroup;
  stepForm: FormGroup;

  // Reference data
  workCenters: WorkCenter[] = [];
  productLines: string[] = [];
  subPSLs: string[] = [];
  bitSeriesList: string[] = [];
  plants: string[] = [];

  // Step categories for grouping
  stepCategories = [
    'Manufacturing',
    'Assembly',
    'Quality Control',
    'Finishing',
    'Inspection',
    'Packaging',
    'Other'
  ];

  // Control keys for SAP
  controlKeys = [
    { value: 'PP01', label: 'PP01 - Internal Processing' },
    { value: 'PP02', label: 'PP02 - External Processing' },
    { value: 'PP03', label: 'PP03 - QC/Inspection' },
    { value: 'PP04', label: 'PP04 - Milestone' }
  ];

  // Row class callback for grid styling
  rowCallback = (context: { dataItem: RouterTemplate }) => {
    return { 'inactive-row': !context.dataItem.isActive };
  };

  constructor(
    private routerAdminService: RouterAdminService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private fb: FormBuilder
  ) {
    this.templateForm = this.fb.group({
      id: [null],
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', Validators.maxLength(500)],
      productLine: [''],
      subPSL: [''],
      bitSeries: [''],
      plant: [''],
      isActive: [true],
      isDefault: [false]
    });

    this.stepForm = this.fb.group({
      id: [null],
      routerTemplateId: [null],
      sequence: [10, [Validators.required, Validators.min(1)]],
      operationCode: [''],
      workCenter: ['', Validators.required],
      controlKey: ['PP01'],
      description: ['', [Validators.required, Validators.maxLength(200)]],
      longText: [''],
      standardTime: [null],
      timeUnit: ['MIN'],
      isRequired: [true],
      isParallel: [false],
      standardTextKey: [''],
      activityType: [''],
      category: ['Manufacturing'],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    this.loadTemplates();
    this.loadReferenceData();
  }

  // ============================================
  // Data Loading
  // ============================================

  loadTemplates(): void {
    this.loading = true;
    this.routerAdminService.getTemplates().subscribe({
      next: (templates) => {
        this.templates = templates;
        this.loading = false;
      },
      error: (err) => {
        this.showNotification('Error loading templates', 'error');
        this.loading = false;
      }
    });
  }

  loadReferenceData(): void {
    this.routerAdminService.getWorkCenters().subscribe({
      next: (wcs) => this.workCenters = wcs,
      error: () => {
        // Provide default work centers if API fails
        this.workCenters = [
          { code: 'MACH01', name: 'Machining Center 1' },
          { code: 'MACH02', name: 'Machining Center 2' },
          { code: 'ASSY01', name: 'Assembly Station 1' },
          { code: 'QC01', name: 'Quality Control' },
          { code: 'FINISH', name: 'Finishing' }
        ];
      }
    });

    this.routerAdminService.getProductLines().subscribe({
      next: (pls) => this.productLines = pls,
      error: () => this.productLines = ['FC', 'RC', 'HART']
    });

    this.routerAdminService.getBitSeries().subscribe({
      next: (bs) => this.bitSeriesList = bs,
      error: () => this.bitSeriesList = ['FX', 'MD', 'SD', 'HD']
    });

    this.routerAdminService.getPlants().subscribe({
      next: (p) => this.plants = p,
      error: () => this.plants = ['1000', '2000', '3000']
    });
  }

  loadSteps(templateId: number): void {
    this.stepsLoading = true;
    this.routerAdminService.getSteps(templateId).subscribe({
      next: (steps) => {
        this.steps = steps.sort((a, b) => a.sequence - b.sequence);
        this.stepsLoading = false;
      },
      error: (err) => {
        this.showNotification('Error loading steps', 'error');
        this.stepsLoading = false;
      }
    });
  }

  onProductLineChange(): void {
    const productLine = this.templateForm.get('productLine')?.value;
    if (productLine) {
      this.routerAdminService.getSubPSLs(productLine).subscribe({
        next: (sps) => this.subPSLs = sps,
        error: () => this.subPSLs = []
      });
    } else {
      this.subPSLs = [];
    }
  }

  // ============================================
  // Template Operations
  // ============================================

  onTemplateSelect(event: any): void {
    if (event.selectedRows?.length > 0) {
      this.selectTemplate(event.selectedRows[0].dataItem);
    }
  }

  selectTemplate(template: RouterTemplate): void {
    this.selectedTemplate = template;
    this.isEditingTemplate = false;
    this.isNewTemplate = false;
    this.cancelStepEdit();
    this.loadSteps(template.id!);
  }

  createNewTemplate(): void {
    this.selectedTemplate = null;
    this.steps = [];
    this.isNewTemplate = true;
    this.isEditingTemplate = true;
    this.templateForm.reset({
      isActive: true,
      isDefault: false
    });
  }

  editTemplate(): void {
    if (this.selectedTemplate) {
      this.isEditingTemplate = true;
      this.isNewTemplate = false;
      this.templateForm.patchValue(this.selectedTemplate);
      this.onProductLineChange();
    }
  }

  cancelTemplateEdit(): void {
    this.isEditingTemplate = false;
    this.isNewTemplate = false;
    this.templateForm.reset();
  }

  saveTemplate(): void {
    if (this.templateForm.invalid) return;

    const template: RouterTemplate = this.templateForm.value;
    this.loading = true;

    if (this.isNewTemplate) {
      this.routerAdminService.createTemplate(template).subscribe({
        next: (created) => {
          this.templates.push(created);
          this.selectedTemplate = created;
          this.isEditingTemplate = false;
          this.isNewTemplate = false;
          this.loading = false;
          this.showNotification('Template created successfully', 'success');
        },
        error: (err) => {
          this.showNotification('Error creating template', 'error');
          this.loading = false;
        }
      });
    } else {
      this.routerAdminService.updateTemplate(template.id!, template).subscribe({
        next: (updated) => {
          const index = this.templates.findIndex(t => t.id === updated.id);
          if (index >= 0) this.templates[index] = updated;
          this.selectedTemplate = updated;
          this.isEditingTemplate = false;
          this.loading = false;
          this.showNotification('Template updated successfully', 'success');
        },
        error: (err) => {
          this.showNotification('Error updating template', 'error');
          this.loading = false;
        }
      });
    }
  }

  deleteTemplate(): void {
    if (!this.selectedTemplate) return;

    const dialogRef = this.dialogService.open({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete the template "${this.selectedTemplate.name}"? This will also delete all associated steps.`,
      actions: [
        { text: 'Cancel' },
        { text: 'Delete', themeColor: 'error' }
      ],
      width: 450
    });

    dialogRef.result.subscribe((result: any) => {
      if (result.text === 'Delete' && this.selectedTemplate) {
        this.routerAdminService.deleteTemplate(this.selectedTemplate.id!).subscribe({
          next: () => {
            this.templates = this.templates.filter(t => t.id !== this.selectedTemplate!.id);
            this.selectedTemplate = null;
            this.steps = [];
            this.showNotification('Template deleted successfully', 'success');
          },
          error: (err) => {
            this.showNotification('Error deleting template', 'error');
          }
        });
      }
    });
  }

  duplicateTemplate(): void {
    if (!this.selectedTemplate) return;

    const dialogRef = this.dialogService.open({
      title: 'Duplicate Template',
      content: 'Enter a name for the new template:',
      actions: [
        { text: 'Cancel' },
        { text: 'Duplicate', themeColor: 'primary' }
      ],
      width: 400
    });

    // Note: In a real implementation, you'd use a custom dialog with an input field
    const newName = `${this.selectedTemplate.name} (Copy)`;
    this.routerAdminService.duplicateTemplate(this.selectedTemplate.id!, newName).subscribe({
      next: (duplicated) => {
        this.templates.push(duplicated);
        this.selectTemplate(duplicated);
        this.showNotification('Template duplicated successfully', 'success');
      },
      error: (err) => {
        this.showNotification('Error duplicating template', 'error');
      }
    });
  }

  // ============================================
  // Step Operations
  // ============================================

  createNewStep(): void {
    if (!this.selectedTemplate) return;

    this.selectedStep = null;
    this.isNewStep = true;
    this.isEditingStep = true;

    // Calculate next sequence number
    const maxSequence = this.steps.length > 0
      ? Math.max(...this.steps.map(s => s.sequence))
      : 0;

    this.stepForm.reset({
      routerTemplateId: this.selectedTemplate.id,
      sequence: maxSequence + 10,
      controlKey: 'PP01',
      timeUnit: 'MIN',
      isRequired: true,
      isParallel: false,
      category: 'Manufacturing',
      isActive: true
    });
  }

  editStep(step: RouterStep): void {
    this.selectedStep = step;
    this.isEditingStep = true;
    this.isNewStep = false;
    this.stepForm.patchValue(step);
  }

  cancelStepEdit(): void {
    this.selectedStep = null;
    this.isEditingStep = false;
    this.isNewStep = false;
    this.stepForm.reset();
  }

  saveStep(): void {
    if (this.stepForm.invalid || !this.selectedTemplate) return;

    const step: RouterStep = this.stepForm.value;
    step.routerTemplateId = this.selectedTemplate.id!;

    this.stepsLoading = true;

    if (this.isNewStep) {
      this.routerAdminService.createStep(this.selectedTemplate.id!, step).subscribe({
        next: (created) => {
          this.steps.push(created);
          this.steps.sort((a, b) => a.sequence - b.sequence);
          this.cancelStepEdit();
          this.stepsLoading = false;
          this.showNotification('Step created successfully', 'success');
        },
        error: (err) => {
          this.showNotification('Error creating step', 'error');
          this.stepsLoading = false;
        }
      });
    } else {
      this.routerAdminService.updateStep(this.selectedTemplate.id!, step.id!, step).subscribe({
        next: (updated) => {
          const index = this.steps.findIndex(s => s.id === updated.id);
          if (index >= 0) this.steps[index] = updated;
          this.steps.sort((a, b) => a.sequence - b.sequence);
          this.cancelStepEdit();
          this.stepsLoading = false;
          this.showNotification('Step updated successfully', 'success');
        },
        error: (err) => {
          this.showNotification('Error updating step', 'error');
          this.stepsLoading = false;
        }
      });
    }
  }

  deleteStep(step: RouterStep): void {
    if (!this.selectedTemplate) return;

    const dialogRef = this.dialogService.open({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete step "${step.description}"?`,
      actions: [
        { text: 'Cancel' },
        { text: 'Delete', themeColor: 'error' }
      ],
      width: 400
    });

    dialogRef.result.subscribe((result: any) => {
      if (result.text === 'Delete') {
        this.routerAdminService.deleteStep(this.selectedTemplate!.id!, step.id!).subscribe({
          next: () => {
            this.steps = this.steps.filter(s => s.id !== step.id);
            if (this.selectedStep?.id === step.id) {
              this.cancelStepEdit();
            }
            this.showNotification('Step deleted successfully', 'success');
          },
          error: (err) => {
            this.showNotification('Error deleting step', 'error');
          }
        });
      }
    });
  }

  moveStepUp(step: RouterStep): void {
    const index = this.steps.findIndex(s => s.id === step.id);
    if (index <= 0) return;

    const prevStep = this.steps[index - 1];
    const tempSeq = step.sequence;
    step.sequence = prevStep.sequence;
    prevStep.sequence = tempSeq;

    this.saveStepSequences();
  }

  moveStepDown(step: RouterStep): void {
    const index = this.steps.findIndex(s => s.id === step.id);
    if (index >= this.steps.length - 1) return;

    const nextStep = this.steps[index + 1];
    const tempSeq = step.sequence;
    step.sequence = nextStep.sequence;
    nextStep.sequence = tempSeq;

    this.saveStepSequences();
  }

  private saveStepSequences(): void {
    this.steps.sort((a, b) => a.sequence - b.sequence);
    const stepIds = this.steps.map(s => s.id!);

    this.routerAdminService.reorderSteps(this.selectedTemplate!.id!, stepIds).subscribe({
      next: () => {
        this.showNotification('Steps reordered', 'success');
      },
      error: () => {
        this.showNotification('Error reordering steps', 'error');
        this.loadSteps(this.selectedTemplate!.id!);
      }
    });
  }

  // ============================================
  // Helpers
  // ============================================

  getWorkCenterName(code: string): string {
    const wc = this.workCenters.find(w => w.code === code);
    return wc ? `${wc.code} - ${wc.name}` : code;
  }

  private showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      type: { style: type, icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'fade', duration: 400 },
      hideAfter: 3000
    });
  }
}
